import { Close as CloseIcon } from '@mui/icons-material';
import { Box, IconButton, Paper, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import { forwardRef } from 'react';

import type { SnackbarKey } from 'notistack';
import type { ReactNode } from 'react';

interface Props {
  description?: ReactNode;
  snackbarKey: SnackbarKey;
  title: ReactNode;
}

/** Dismissible card for enqueueSnackbar's `content` render-prop — use when a
 *  plain text snackbar isn't enough (e.g. a bold title plus a longer
 *  description). Forwards its ref to the root Paper: notistack's Slide
 *  transition requires the immediate child of `content` to be able to hold
 *  a ref.
 *
 *  const key = enqueueSnackbar('Draft saved', {
 *    content: (key) => (
 *      <SnackbarContent
 *        description="Publish it from here when it's ready."
 *        snackbarKey={key}
 *        title="Draft saved"
 *      />
 *    ),
 *  }); */
const SnackbarContent = forwardRef<HTMLDivElement, Props>(
  ({ description, snackbarKey, title }, ref) => {
    const { closeSnackbar } = useSnackbar();

    return (
      <Paper
        elevation={3}
        ref={ref}
        sx={{
          borderLeft: (theme) => `4px solid ${theme.palette.primary.main}`,
          borderRadius: 1,
          display: 'flex',
          gap: 1,
          justifyContent: 'space-between',
          maxWidth: 400,
          p: 2,
        }}
      >
        <Box>
          <Typography fontWeight={700}>{title}</Typography>
          {description && (
            <Typography variant="body2">{description}</Typography>
          )}
        </Box>
        <IconButton
          onClick={() => closeSnackbar(snackbarKey)}
          size="small"
          sx={{ mt: -0.5 }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Paper>
    );
  },
);

SnackbarContent.displayName = 'SnackbarContent';

export default SnackbarContent;
